import BoxCard from "./BoxCard";

function HomePage({
  labels,
  roofBoxes,
  accessories,
  skiSnowboardProducts = [],
  pricingBoxes,
  canScrollProductsLeft,
  canScrollProductsRight,
  productCarouselRef,
  onScrollProducts,
  onProductCarouselWheel,
  accessoryCarouselRef,
  canScrollAccessoriesLeft,
  canScrollAccessoriesRight,
  onScrollAccessories,
  onAccessoryCarouselWheel,
  skiCarouselRef,
  canScrollSkiLeft,
  canScrollSkiRight,
  onScrollSki,
  onOpenBox
}) {
  const faqItems = labels.faq.items ?? [];
  const faqSchema = faqItems.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqItems.map(item => ({
          "@type": "Question",
          name: item.title,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.text
          }
        }))
      }
    : null;

  return (
    <>
      <section className="hero-section reveal-on-scroll" id="top">
        <div className="hero-copy reveal-on-scroll">
          <p className="eyebrow">{labels.hero.eyebrow}</p>
          <h1 className="hero-service-list">
            {(labels.hero.titleLines ?? [labels.hero.title]).map(line => (
              <span key={line}>{line}</span>
            ))}
          </h1>
          {labels.hero.text ? <p className="hero-lead">{labels.hero.text}</p> : null}
        </div>

        <div className="hero-visual reveal-on-scroll">
          <img
            src="/pictures/header_image.avif"
            alt={labels.hero.imageAlt}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        </div>
      </section>

      <section className="listing-section reveal-on-scroll" id="roof-boxes">
        <div className="section-heading reveal-on-scroll">
          <div>
            <p className="section-kicker">{labels.products.kicker}</p>
            {labels.products.title ? <h2>{labels.products.title}</h2> : null}
          </div>
        </div>

        <div
          className="product-carousel-shell reveal-on-scroll"
          data-has-carousel-preview-left={canScrollProductsLeft}
          data-has-carousel-preview-right={canScrollProductsRight}
        >
          {canScrollProductsLeft && (
            <button
              type="button"
              className="carousel-arrow carousel-arrow-left"
              onClick={() => onScrollProducts("previous")}
              aria-label={labels.products.scrollPrevious}
            >
              <span className="carousel-arrow-icon carousel-arrow-icon-left" aria-hidden="true" />
            </button>
          )}

          {canScrollProductsRight && (
            <button
              type="button"
              className="carousel-arrow carousel-arrow-right"
              onClick={() => onScrollProducts("next")}
              aria-label={labels.products.scrollNext}
            >
              <span className="carousel-arrow-icon carousel-arrow-icon-right" aria-hidden="true" />
            </button>
          )}

          <div
            ref={productCarouselRef}
            className="product-carousel"
            role="region"
            aria-label={labels.products.title}
            onWheel={onProductCarouselWheel}
          >
            {roofBoxes.map(box => (
              <BoxCard
                key={box.id}
                box={box}
                onRent={onOpenBox}
                labels={labels.boxCard}
              />
            ))}
          </div>
        </div>

        {accessories.length > 0 && (
          <div className="accessory-section" id="bike-racks">
            <div className="section-heading">
              <div>
                <p className="section-kicker">{labels.accessories.kicker}</p>
                {labels.accessories.title ? <h2>{labels.accessories.title}</h2> : null}
              </div>
            </div>

            <div
              className="product-carousel-shell accessory-carousel-shell"
              data-has-carousel-preview-left={canScrollAccessoriesLeft}
              data-has-carousel-preview-right={canScrollAccessoriesRight}
            >
              {canScrollAccessoriesLeft && (
                <button
                  type="button"
                  className="carousel-arrow carousel-arrow-left accessory-carousel-arrow"
                  onClick={() => onScrollAccessories("previous")}
                  aria-label={labels.products.scrollPrevious}
                >
                  <span className="carousel-arrow-icon carousel-arrow-icon-left" aria-hidden="true" />
                </button>
              )}

              {canScrollAccessoriesRight && (
                <button
                  type="button"
                  className="carousel-arrow carousel-arrow-right accessory-carousel-arrow"
                  onClick={() => onScrollAccessories("next")}
                  aria-label={labels.products.scrollNext}
                >
                  <span className="carousel-arrow-icon carousel-arrow-icon-right" aria-hidden="true" />
                </button>
              )}

              <div
                ref={accessoryCarouselRef}
                className="box-grid accessory-grid"
                role="region"
                aria-label={labels.accessories.kicker}
                onWheel={onAccessoryCarouselWheel}
              >
                {accessories.map(box => (
                  <BoxCard
                    key={box.id}
                    box={box}
                    onRent={onOpenBox}
                    labels={labels.boxCard}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {skiSnowboardProducts.length > 0 && (
          <div className="accessory-section" id="ski-snowboard">
            <div className="section-heading">
              <div>
                <p className="section-kicker">{labels.skiSnowboard.kicker}</p>
                {labels.skiSnowboard.title ? <h2>{labels.skiSnowboard.title}</h2> : null}
              </div>
            </div>

            <div
              className="product-carousel-shell"
              data-has-carousel-preview-left={canScrollSkiLeft}
              data-has-carousel-preview-right={canScrollSkiRight}
            >
              {canScrollSkiLeft && (
                <button type="button" className="carousel-arrow carousel-arrow-left"
                  onClick={() => onScrollSki("previous")} aria-label={labels.products.scrollPrevious}>
                  <span className="carousel-arrow-icon carousel-arrow-icon-left" aria-hidden="true" />
                </button>
              )}
              {canScrollSkiRight && (
                <button type="button" className="carousel-arrow carousel-arrow-right"
                  onClick={() => onScrollSki("next")} aria-label={labels.products.scrollNext}>
                  <span className="carousel-arrow-icon carousel-arrow-icon-right" aria-hidden="true" />
                </button>
              )}
              <div
                ref={skiCarouselRef}
                className="product-carousel"
                role="region"
                aria-label={labels.skiSnowboard.kicker}
                onWheel={onAccessoryCarouselWheel}
              >
                {skiSnowboardProducts.map(box => (
                  <BoxCard
                    key={`ski-${box.id}`}
                    box={box}
                    onRent={onOpenBox}
                    labels={labels.boxCard}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="info-section reveal-on-scroll" id="prices">
        <div className="section-heading reveal-on-scroll">
          <div>
            <p className="section-kicker">{labels.prices.kicker}</p>
            {labels.prices.title ? <h2>{labels.prices.title}</h2> : null}
          </div>
        </div>

        <div className="pricing-table-wrapper reveal-on-scroll">
          <table className="pricing-table">
            <thead>
              <tr>
                <th>{labels.prices.columns.product}</th>
                <th>{labels.prices.columns.dailyPrice}</th>
                <th>{labels.prices.columns.volume}</th>
                <th>{labels.prices.columns.deposit}</th>
                <th>{labels.prices.columns.recommendation}</th>
              </tr>
            </thead>
            <tbody>
              {pricingBoxes.map(box => (
                  <tr key={`price-${box.id}`}>
                    <td>{box.name}</td>
                    <td><strong>{box.price} {labels.boxCard.pricePerDay}</strong></td>
                    <td>{box.category === "roof-box" ? `${box.volume} ${labels.boxCard.liters}` : box.capacityLabel ?? "-"}</td>
                    <td>{box.depositAmount} {labels.admin.currency}</td>
                    <td>
                      {box.recommendedFor}
                      {box.category === "roof-rail" && labels.prices.wingBarKitNote ? (
                        <span className="pricing-note">{labels.prices.wingBarKitNote}</span>
                      ) : null}
                    </td>
                  </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="info-section reveal-on-scroll" id="faq">
        <div className="section-heading reveal-on-scroll">
          <div>
            <p className="section-kicker">{labels.faq.kicker}</p>
            {labels.faq.title ? <h2>{labels.faq.title}</h2> : null}
          </div>
        </div>

        <div className="faq-list">
          {faqItems.map(item => (
            <details className="faq-item reveal-on-scroll" key={item.title}>
              <summary>
                <span>{item.title}</span>
              </summary>
              <div className="faq-answer">
                <p>{item.text}</p>
              </div>
            </details>
          ))}
        </div>

        {faqSchema ? (
          <script type="application/ld+json">
            {JSON.stringify(faqSchema)}
          </script>
        ) : null}
      </section>

      <section className="info-section reveal-on-scroll" id="contact">
        <div className="section-heading reveal-on-scroll">
          <div>
            <p className="section-kicker">{labels.contact.kicker}</p>
            {labels.contact.title ? <h2>{labels.contact.title}</h2> : null}
          </div>
        </div>

        <div className="contact-card reveal-on-scroll">
          <p>
            {labels.contact.phone}:{" "}
            <a href={`tel:${labels.contact.phoneValue.replace(/\s+/g, "")}`}>
              <strong>{labels.contact.phoneValue}</strong>
            </a>
          </p>
          <p>
            {labels.contact.email}:{" "}
            <a href={`mailto:${labels.contact.emailValue}`}>
              <strong>{labels.contact.emailValue}</strong>
            </a>
          </p>
          <p>
            {labels.contact.pickup}: <strong>{labels.contact.pickupValue}</strong>
          </p>
        </div>
      </section>
    </>
  );
}

export default HomePage;
